import React, { createContext, ReactNode } from "react";
import { useGetTranslations } from "../hooks/useGetTranslations";

export type i18nParams = Record<string, string | undefined>;

type TranslationsContextType = {
  t: (key: string, params?: i18nParams, escapeChars?: string[]) => string;
  translations: Record<string, string> | undefined;
  translateWithFormat: (key: string, params?: i18nParams, escapeChars?: string[]) => string;
};

export const TranslationsContext = createContext<TranslationsContextType>(
  {} as TranslationsContextType,
);

export const TranslationsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { data: translations } = useGetTranslations();

  function escape(value: string, escapeChars: string[]) {
    return escapeChars.reduce((pre, cur) => pre.replaceAll(cur, `\\${cur}`), value);
  }

  function replaceValues(value: string, params: i18nParams, escapeChars: string[]) {
    let result = "";
    let currentPosition = 0;
    for (const match of value.matchAll(/{{(?<key>.*?)}}/g)) {
      const key = match.groups?.key ?? "";
      result += value.substring(currentPosition, match.index);
      currentPosition = match.index ?? 0;
      const param = params[key];
      // translation of the param t(param) is in case
      // the param is a key in the translations object
      // e.g. month in a payslip
      if (param) result += escape(t(param), escapeChars);
      currentPosition += match[0].length;
    }
    result += value.substring(currentPosition);
    return result;
  }

  function unescape(value: string, escapeChars: string[]) {
    return escapeChars.reduce((pre, cur) => pre.replaceAll(`\\${cur}`, cur), value);
  }
  const t = (key: string, params: i18nParams = {}, escapeChars: string[] = []) => {
    if (!key) return "";

    let value = translateWithFormat(key, params, escapeChars);
    let result = [];
    let currentPosition = 0;

    for (const match of value.matchAll(/\*\*(?<val>.*?)\*\*/g)) {
      result.push(unescape(value.substring(currentPosition, match.index), escapeChars));
      currentPosition = match.index ?? 0;
      result.push(unescape(match.groups?.val ?? "", escapeChars));
      currentPosition += match[0].length;
    }
    result.push(unescape(value.substring(currentPosition), escapeChars));

    value = result.join("");
    result = [];
    currentPosition = 0;

    for (const match of value.matchAll(/\[(?<val>.*?)\]\((?<lnk>.*?)\)/g)) {
      result.push(unescape(value.substring(currentPosition, match.index), escapeChars));
      currentPosition = match.index ?? 0;
      result.push(unescape(match.groups?.val ?? "", escapeChars));
      currentPosition += match[0].length;
    }
    result.push(unescape(value.substring(currentPosition), escapeChars));
    return result.join("");
  };

  const translateWithFormat = (
    key: string,
    params: i18nParams = {},
    escapeChars: string[] = [],
  ) => {
    if (!translations || !translations[key]) return key;
    const value = translations[key];
    return replaceValues(value, params, escapeChars);
  };

  const value = {
    t,
    translations,
    translateWithFormat,
  };

  return <TranslationsContext.Provider value={value}>{children}</TranslationsContext.Provider>;
};
